import { Injectable } from '@angular/core';
import { fromEvent, interval, merge, Observable, Subject } from 'rxjs';
import { debounceTime, filter, take, takeUntil } from 'rxjs/operators';

/**
 * SAN-497 — shared profile-completion-form auto-save helper.
 *
 * One reusable mechanism implementing:
 *  1. A fixed-interval (default 20s, decision 6) dirty-gated save (decision 2).
 *  2. A best-effort `beforeunload`/`visibilitychange` save (decision 1).
 *  3. Lifecycle scoping to the mounting component — the interval and the
 *     window/document listeners are torn down on `destroy()` (decision 5),
 *     which a host component wires to its own `ngOnDestroy()` (either
 *     explicitly, or implicitly by passing its existing `destroyed$` Subject
 *     as `destroyed$` below, reusing the pattern already used throughout
 *     this codebase for `takeUntil()`).
 *
 * This helper deliberately does NOT perform any HTTP call itself — `save`
 * (and `beforeUnloadSave`, when different) must be supplied by the caller
 * and MUST go through the exact existing `HttpClient`-backed service method
 * the removed manual Save button used to call. Never wire a raw `fetch()`/
 * `navigator.sendBeacon()` into `save`/`beforeUnloadSave` — neither reliably
 * carries the cross-origin `accessToken` auth cookie (`/check-isolation`
 * gate item 1), unlike `HttpClient`, which has `withCredentials: true` set
 * globally for this app.
 *
 * Callers remain responsible for:
 *  - their own in-flight/re-entrancy guard around `save`/`beforeUnloadSave`
 *    (this helper only decides WHEN to attempt a save, never whether one is
 *    already outstanding) — see `EventFormsComponent` and
 *    `StartupInformationComponent` for the two call sites this was a real
 *    requirement for (`/check-isolation` gate items 2-3).
 *  - calling `markAsPristine()` (or the equivalent for non-`FormGroup`
 *    dirty state) inside their own save-success handling, so `isDirty()`
 *    has an accurate baseline for the next tick (decision 2).
 */
/**
 * Implemented by a host component that registers an `AutoSaveHandle`, so a
 * caller outside the component (e.g. `UnsavedProfileLeaveGuard`) can force
 * an immediate flush of whatever is currently dirty — without needing to
 * know which save method or form this particular step wires up internally.
 */
export interface HasPendingAutoSave {
  flushPendingAutoSave(): void;
}

export interface AutoSaveConfig {
  /** Returns true if there is unsaved, dirty state to persist right now. */
  isDirty: () => boolean;
  /** Performs the periodic auto-save. Must reuse the existing service call. */
  save: () => void;
  /**
   * Performs the best-effort `beforeunload`/`visibilitychange` save.
   * Defaults to `save` when omitted. Only needed when the tab-close save
   * must invoke a different call than the periodic tick — e.g. Startup's
   * aggregate `onSubmit()` (decision 4) vs. its 5 decomposed per-section
   * interval saves.
   */
  beforeUnloadSave?: () => void;
  /**
   * Optional — an `Observable` (e.g. the host component's own `destroyed$`
   * `Subject`) whose `next`/`complete` tears this helper down automatically.
   * If omitted, the host component MUST call `AutoSaveHandle.destroy()`
   * itself from `ngOnDestroy()`.
   */
  destroyed$?: Observable<unknown>;
  /** Auto-save interval in ms. Defaults to 20000 (decision 6). */
  intervalMs?: number;
  /**
   * Optional — an `Observable` that emits on every user edit (e.g. the
   * host form's `valueChanges`, merged across sub-forms if there are
   * several). When supplied, a save is attempted a short debounce after the
   * last emission, in addition to the fixed interval and beforeunload
   * paths — so a field the user just filled in is persisted almost
   * immediately instead of waiting up to a full interval period. Still
   * gated by `isDirty()` and the same in-flight bracketing as every other
   * save path; a burst of keystrokes collapses into one save after the
   * user pauses, not one save per keystroke.
   */
  changes$?: Observable<unknown>;
  /** Debounce for the `changes$`-triggered save, in ms. Defaults to 800. */
  debounceMs?: number;
}

export class AutoSaveHandle {
  /**
   * SAN-511 — lets `AddTokenHeaderHttpRequestInterceptor` tell an
   * auto-save-originated HTTP request apart from a manual/foreground one,
   * WITHOUT threading a flag through every `*.service.ts` method the 18
   * auto-save call sites use. Angular's HttpClient interceptor pipeline runs
   * synchronously up to request dispatch (only the response is async), so
   * `attemptSave()` incrementing this synchronously right before calling
   * `save()`/`beforeUnloadSave()` — and decrementing right after — means
   * `intercept()` sees an accurate count at request-time; it captures the
   * value in a local const for its own later async `catchError` to read
   * (reading the live counter from inside `catchError` itself would be
   * wrong, since by then this method's `finally` has already decremented it
   * back down).
   */
  private static inFlightRequestCount = 0;

  static isAutoSaveRequestInFlight(): boolean {
    return AutoSaveHandle.inFlightRequestCount > 0;
  }

  private readonly stop$ = new Subject<void>();
  private destroyed = false;

  constructor(private readonly config: AutoSaveConfig) {
    const intervalMs = config.intervalMs ?? 10000;

    // 1) Fixed-interval, dirty-gated tick (decisions 2 + 6).
    interval(intervalMs)
      .pipe(takeUntil(this.stop$))
      .subscribe(() => this.attemptSave(this.config.save));

    // 2) One best-effort, dirty-gated save on tab hide/close (decision 1).
    //    `visibilitychange` covers mobile/backgrounding + most tab-close
    //    paths; `beforeunload` covers the remaining desktop close/navigate
    //    cases. Either firing triggers at most one attempt.
    merge(
      fromEvent(window, 'beforeunload'),
      fromEvent(document, 'visibilitychange').pipe(
        filter(() => document.visibilityState === 'hidden')
      )
    )
      .pipe(takeUntil(this.stop$))
      .subscribe(() => this.attemptSave(this.config.beforeUnloadSave ?? this.config.save));

    // 3) Debounced near-immediate save on user edit, when the caller wires
    //    one up (decision: fast-path a field the user just filled in
    //    instead of only ever catching it on the next interval tick).
    if (config.changes$) {
      config.changes$
        .pipe(debounceTime(config.debounceMs ?? 800), takeUntil(this.stop$))
        .subscribe(() => this.attemptSave(this.config.save));
    }

    if (config.destroyed$) {
      config.destroyed$.pipe(take(1)).subscribe({
        next: () => this.destroy(),
        complete: () => this.destroy(),
      });
    }
  }

  /**
   * Attempts a save right now, immediately, if the form is currently dirty —
   * same dirty-check + in-flight bracketing as a periodic tick, just not on
   * the timer's own schedule. Used when the user is about to leave the page
   * a moment before the interval would have ticked (e.g. the SAN-499
   * leave-confirm dialog's Discard action), so an edit made less than one
   * interval period ago isn't silently lost. Fire-and-forget, same as the
   * existing beforeunload/visibilitychange path — does not block navigation
   * on the HTTP response completing.
   */
  flush(): void {
    this.attemptSave(this.config.save);
  }

  private attemptSave(save: () => void): void {
    if (this.destroyed) {
      return;
    }
    if (this.config.isDirty()) {
      // SAN-511 — bracket the synchronous portion of the HTTP call (request
      // construction through interceptor dispatch) so the interceptor can
      // tell this was an auto-save call. See `inFlightRequestCount` above.
      AutoSaveHandle.inFlightRequestCount++;
      try {
        save();
      } finally {
        AutoSaveHandle.inFlightRequestCount--;
      }
    }
  }

  /**
   * Tears down the interval and the `beforeunload`/`visibilitychange`
   * listeners. Idempotent — safe to call more than once (e.g. once via a
   * supplied `destroyed$` and once explicitly from `ngOnDestroy()`).
   */
  destroy(): void {
    if (this.destroyed) {
      return;
    }
    this.destroyed = true;
    this.stop$.next();
    this.stop$.complete();
  }
}

@Injectable({ providedIn: 'root' })
export class AutoSaveService {
  /** Starts a new auto-save cycle for one mounted component/form. */
  register(config: AutoSaveConfig): AutoSaveHandle {
    return new AutoSaveHandle(config);
  }
}
